'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import { Button, FormField, Input, LinkButton, Text } from '@/components/ui'
import Yup from '@/lib/utils/yup'
import { InferType } from 'yup'
import { MessageSeverity, useAppStore } from '@/store'
import { apiClient } from '@/config/config'
import { ClientRoutes } from '@/config'
import { useLocalizedRoutes } from '@/lib/hooks'

export enum Fields {
  username = 'username',
  password = 'password',
  email = 'email',
}

export const signUpFormSchema = Yup.object({
  [Fields.username]: Yup.string().min(3).required(),
  [Fields.password]: Yup.string().min(6).required(),
  [Fields.email]: Yup.string().email().required(),
})

export const formName = 'SignUpForm'

export { Fields as SignUpFormFields }

export type SignUpFormSchemaType = InferType<typeof signUpFormSchema>

export interface UserSignUpProps {
  username: string
  password: string
  email: string
}

/* Component */
export const SignUpForm: React.FC<{
  showOptions?: boolean
  onFinish?: () => void
}> = ({ showOptions, onFinish }) => {
  const form = useForm<SignUpFormSchemaType>({
    mode: 'onTouched',
    resolver: yupResolver(signUpFormSchema),
  })

  const { control, handleSubmit } = form

  const router = useRouter()

  const t = useTranslations('auth.signUpForm')
  const { get: _ } = useLocalizedRoutes()

  const {
    setUserVerifyEmail,
    status: { loading, isSignUpVisible },
    locale,
    setLoading,
    setMessage,
  } = useAppStore()

  const onSubmitValid: SubmitHandler<SignUpFormSchemaType> = useCallback(
    async (data) => {
      setLoading(true)
      try {
        const signUpResponse = await apiClient.postSignup({
          email: data.email,
          name: data.username,
          password: data.password,
        })

        if (signUpResponse?.data?.expiresAt) {
          setUserVerifyEmail(data.email)
          if (!isSignUpVisible) {
            router.replace(`${locale}/${ClientRoutes.verifyPin}`)
          }
        }
      } catch (error) {
        setMessage({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          message: t((error as any)?.message ?? 'unknownError'),
          severity: MessageSeverity.ERROR,
        })
      } finally {
        onFinish?.()
        setLoading(false)
      }
    },
    [
      isSignUpVisible,
      locale,
      onFinish,
      router,
      setLoading,
      setMessage,
      setUserVerifyEmail,
      t,
    ],
  )

  const onClickSubmit = useCallback(() => {
    handleSubmit(onSubmitValid, () => {
      setMessage({
        message: t('invalidForm'),
        severity: MessageSeverity.ERROR,
      })
    })()
  }, [handleSubmit, onSubmitValid, setMessage, t])

  return (
    <FormProvider {...form}>
      <div className="w-full">
        <div className="space-y-1 flex flex-col w-full" />
        <div className="space-y-4 text-center">
          <Text variant="heading2Xl" className="text-white my-16">
            {t('title')}
          </Text>

          <div className="space-y-2">
            <FormField
              control={control}
              name={Fields.username}
              render={({ field }) => (
                <Input
                  type="text"
                  {...field}
                  placeholder={t('fields.username')}
                  className="px-6 py-4 h-12 w-full text-xl rounded-full text-base placeholder:text-base bg-primary text-white border-white"
                />
              )}
            />
          </div>
          <div className="space-y-2">
            <FormField
              control={control}
              name={Fields.email}
              render={({ field }) => (
                <Input
                  type="email"
                  {...field}
                  placeholder={t('fields.email')}
                  className="px-6 py-4 h-12 w-full text-xl rounded-full text-base placeholder:text-base bg-primary text-white border-white"
                />
              )}
            />
          </div>
          <div className="space-y-2">
            <FormField
              control={control}
              name={Fields.password}
              render={({ field }) => (
                <Input
                  type="password"
                  {...field}
                  placeholder={t('fields.password')}
                  className="px-6 py-4 h-12 w-full text-xl rounded-full text-base placeholder:text-base bg-primary text-white border-white"
                />
              )}
            />
          </div>
        </div>
        <div className="flex flex-col align-self-end">
          <Button
            disabled={loading}
            onClick={() => onClickSubmit()}
            size="lg"
            variant="glow"
            className="w-auto p-6 text-lg my-6 rounded-full text-primary"
          >
            {t('buttonCaption')}
          </Button>
        </div>
      </div>
      {showOptions && (
        <div className="mt-4 text-sm text-center">
          <p className="mb-5 text-lg text-white">{t('alreadyHaveAccount')}</p>
          <LinkButton
            variant="outline"
            size="lg"
            className="rounded-full text-lg py-6 px-12"
            href={_(ClientRoutes.signIn)}
          >
            {t('signIn')}
          </LinkButton>
        </div>
      )}
    </FormProvider>
  )
}
